import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import _ from 'underscore';

import { useActions } from 'hooks/useActions';
import { searchQueryFilter, tagFilter, ratingFilter } from 'functions/filters';
import { sortFunc } from 'functions/sort';
import { userRoles } from 'constants/users';
import { Container, Wrapper } from 'components/Layout/Layout';
import RoundedSearchBar from 'components/RoundedSearchBar/RoundedSearchBar';
import CustomSorter from 'components/CustomSorter/CustomSorter';
import FilterDropdown from 'components/FilterDropdown/FilterDropdown';
import CommunityCard from 'components/CommunityCard/CommunityCard';
import { CommunityCardGrid } from 'components/CommunityCard/styles';
import * as actions from './actions';
import { useQueryParams } from 'hooks/useQueryParams';
import { getCookie } from 'functions/getCookie';
import LoaderCard from './component/LoaderCard';
import {
  ChallengeListBackgroundImage,
  HeaderAndContentContainer,
  Header,
  HeaderContent,
  Icon,
  CreateButton,
  FiltersContainer,
  DropdownAndSearchContainer,
} from './styles';

const EXERCISES = 'exercises';
const CHALLENGES = 'challenges';
const COURSES = 'courses';

export default function ChallengeListPage() {
  const { c: campaignSource } = useQueryParams();
  const { pathname } = useLocation();
  const type = pathname.split('/')[1];

  const { userRole, isCommunityUser } = useSelector((state) => state.auth);

  const { getAllExercises, getAllChallenges, getAllCourses } = useActions(actions);
  const { loading, error, itemsList } = useSelector((state) => state.CommunityListPageReducer);

  const [items, setItems] = useState([]);
  const [hasFilters, setHasFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedRating, setSelectedRating] = useState(undefined);
  const [sortBy, setSortBy] = useState({});
  const [gridSize, setGridSize] = useState(type === COURSES ? 2 : 4);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    const updateGridSize = () => {
      if (type === COURSES) return setGridSize(2);
      if (window.innerWidth < 767) return setGridSize(1);
      if (window.innerWidth < 1024) return setGridSize(2);
      if (window.innerWidth < 1366) return setGridSize(3);
      setGridSize(4);
    };

    updateGridSize();
    window.addEventListener('resize', updateGridSize);

    return () => {
      window.addEventListener('resize', updateGridSize);
    };
  }, [type]);

  useEffect(() => {
    // default filters
    setSelectedTags(['Official']);
    setSelectedRating(3);
    setSearchQuery('');
    setHasFilters(false);
    setSortBy({
      value: type === COURSES ? 'name' : 'averageRating',
      descendingOrder: true,
    });
    setFilterOptions([]);

    const isKodcentrumCampaign = (campaignSource || getCookie('campaignSource')) === 'kodcentrum';

    if (type === EXERCISES) getAllExercises(isKodcentrumCampaign);
    if (type === CHALLENGES) getAllChallenges();
    if (type === COURSES) getAllCourses(isKodcentrumCampaign);
  }, [type, getAllExercises, getAllChallenges, getAllCourses, campaignSource]);

  useEffect(() => {
    if (itemsList?.length) setItems(itemsList.slice());
  }, [itemsList]);

  useEffect(() => {
    let filteredItems = itemsList.filter(searchQueryFilter(searchQuery)).filter(tagFilter(selectedTags));
    if (type !== COURSES) filteredItems = filteredItems.filter(ratingFilter(selectedRating));
    setItems(filteredItems);
    setHasFilters(selectedTags.length > 0 || !!selectedRating);
  }, [itemsList, searchQuery, selectedTags, selectedRating, type]);

  useEffect(() => {
    if (!itemsList) return;

    const difficultyTags = ['Easy', 'Medium', 'Hard'];
    let hasDifficultyTags = false;
    let tagItems = new Set();

    for (const item of itemsList) {
      for (const tag of item.keywords) {
        if (difficultyTags.includes(tag)) {
          hasDifficultyTags = true;
        } else {
          tagItems.add(tag);
        }
      }
    }

    tagItems = Array.from(tagItems).sort();

    let newOptions = [];

    if (tagItems.length) {
      newOptions.push({
        title: 'Tag',
        items: tagItems,
        type: 'keywords',
        selectedKeywords: selectedTags,
        onChange: (selection) => setSelectedTags(selection),
      });
    }

    if (hasDifficultyTags) {
      newOptions.push({
        title: 'Difficulty',
        items: difficultyTags,
        type: 'keywords',
        selectedKeywords: selectedTags,
        onChange: (selection) => setSelectedTags(selection),
      });
    }

    if (type !== COURSES)
      newOptions.push({
        title: 'Rating',
        type: 'rating',
        selectedRating,
        onChange: (selection) => setSelectedRating(selection),
      });

    setFilterOptions(newOptions);
  }, [itemsList, selectedTags, selectedRating, type]);

  const clearFilters = (e) => {
    e.stopPropagation();
    setSelectedTags([]);
    setSelectedRating(undefined);
  };

  return (
    <Wrapper key={type} isNewPage>
      <ChallengeListBackgroundImage />
      <Container column useZIndex="1">
        <HeaderAndContentContainer>
          <Header>{type}</Header>
          <HeaderContent>
            {type === EXERCISES
              ? 'Practice your coding skills by solving exercises'
              : type === CHALLENGES
                ? 'Compete against the community to ascend the leaderboard'
                : 'Master new skills with in-depth learning paths'}
          </HeaderContent>
          {userRole === userRoles.CANDIDATE && isCommunityUser && type !== COURSES && (
            <CreateButton to="/customTests">
              <Icon className="fa-solid fa-circle-plus" /> Create {type === EXERCISES ? 'Exercise' : 'Challenge'}
            </CreateButton>
          )}
        </HeaderAndContentContainer>
        <FiltersContainer>
          <FilterDropdown
            type={type}
            items={items}
            selectedTags={selectedTags}
            hasFilters={hasFilters}
            clearFilters={clearFilters}
            options={filterOptions}
          />
          <DropdownAndSearchContainer>
            <CustomSorter
              showLabel={'Sort by '}
              value={sortBy.value}
              descendingOrder={sortBy.descendingOrder}
              options={
                type === COURSES
                  ? [
                      { value: 'name', label: 'Name' },
                      { value: 'createdAt', label: 'Newest' },
                    ]
                  : [
                      { value: 'name', label: 'Name' },
                      { value: 'createdAt', label: 'Newest' },
                      { value: 'averageRating', label: 'Rating' },
                      { value: 'totalCompletions', label: 'Participants' },
                    ]
              }
              onChange={(value) => setSortBy(value)}
            />
            <RoundedSearchBar value={searchQuery} onChange={setSearchQuery} />
          </DropdownAndSearchContainer>
        </FiltersContainer>
        {loading ? (
          <CommunityCardGrid>
            {_.range(gridSize).map((val) => (
              <LoaderCard key={`grid${val}`} />
            ))}
          </CommunityCardGrid>
        ) : (
          <CommunityCardGrid>
            {items
              .sort((a, b) => sortFunc(a, b, sortBy))
              .map((item) => (
                <CommunityCard
                  key={item._id}
                  pageType={type}
                  link={`/${type}/${item._id}`}
                  tags={item.keywords}
                  {...item}
                />
              ))}
          </CommunityCardGrid>
        )}
      </Container>
    </Wrapper>
  );
}
